<template>
    <div>
        <NavbarOne/>

        <div class="flex items-center gap-4 flex-wrap bg-overlay p-14 sm:p-16 before:bg-title before:bg-opacity-70" :style="{backgroundImage:'url(' + bg + ')'}">
            <div class="text-center w-full">
                <h2 class="text-white text-8 md:text-[40px] font-normal leading-none text-center">Portfolio</h2>
                <ul class="flex items-center justify-center gap-[10px] text-base md:text-lg leading-none font-normal text-white mt-3 md:mt-4">
                    <li><router-link to="/">Home</router-link></li>
                    <li>/</li>
                    <li class="text-primary">Portfolio</li>
                </ul>
            </div>
        </div>

        <div class="py-16 overflow-hidden relative" data-aos="fade-up">
            <p v-if="loading" class="text-center text-paragraph dark:text-white-light">Loading portfolios...</p>
            <p v-else-if="error" class="text-center text-red-500">{{ error }}</p>
            <p v-else-if="!portfolios.length" class="text-center text-paragraph dark:text-white-light">No portfolios published yet.</p>
            <div v-else class="max-w-[1720px] mx-auto px-[15px] sm:px-12">
                <div class="mb-8 flex flex-wrap items-center gap-2 sm:gap-3">
                    <button
                        v-for="tab in categoryTabs"
                        :key="tab"
                        type="button"
                        class="rounded-full border px-4 py-2 text-sm font-medium leading-none transition sm:px-5 sm:py-2.5"
                        :class="activeCategory === tab
                            ? 'border-primary bg-primary text-white'
                            : 'border-line bg-white text-title hover:border-primary hover:text-primary dark:border-line-dark dark:bg-title dark:text-white dark:hover:border-primary dark:hover:text-primary'"
                        @click="activeCategory = tab"
                    >
                        {{ tab }}
                    </button>
                </div>

                <p v-if="!filteredPortfolios.length" class="py-10 text-center text-paragraph dark:text-white-light">
                    No portfolios in this category yet.
                </p>

                <div v-else class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 sm:gap-8">
                <router-link
                    v-for="item in filteredPortfolios"
                    :key="item.id"
                    :to="`/portfolio-details-v1/${item.id}`"
                    class="portfolio-card relative group overflow-hidden block aspect-[4/3] sm:aspect-[4/5] bg-[#F6F2EC] rounded-[6px]"
                >
                    <img class="w-full h-full object-cover transition duration-500 group-hover:scale-105" :src="item.main_image_url || fallbackImage" alt="Portfolio"/>
                    <div class="absolute inset-0 bg-black/0 transition duration-500 group-hover:bg-black/65"></div>
                    <div class="absolute inset-0 bg-gradient-to-t from-black/95 via-black/65 to-black/20 opacity-0 transition duration-500 group-hover:opacity-100"></div>
                    <div class="absolute inset-x-0 bottom-0 z-10 p-4 sm:p-6 md:p-7">
                        <div class="translate-y-3 sm:translate-y-5 opacity-95 transition duration-500 group-hover:translate-y-0">
                            <span v-if="item.category" class="inline-block text-[12px] sm:text-[15px] leading-none text-title font-medium px-2.5 py-1.5 sm:p-[10px] bg-[#DBCBBD] rounded-md">
                            {{ item.category }}
                            </span>
                            <span class="block text-base sm:text-lg md:text-xl font-semibold leading-tight text-white mt-2 sm:mt-3 md:mt-4">
                                {{ item.title }}
                            </span>
                        </div>
                        <p
                            v-if="item.short_description"
                            class="mt-2 sm:mt-3 max-w-[28ch] text-[13px] sm:text-sm leading-5 sm:leading-6 text-white/85 overflow-hidden max-h-0 opacity-0 translate-y-3 transition-all duration-500 group-hover:max-h-24 group-hover:opacity-100 group-hover:translate-y-0"
                        >
                            {{ item.short_description }}
                        </p>
                    </div>
                </router-link>
                </div>
            </div>
        </div>

        <FooterOne/>

        <ScrollToTop/>

    </div>

</template>

<script setup>
    import { computed, onMounted, ref } from 'vue';
    import NavbarOne from '@/components/navbar/navbar-one.vue';
    import FooterOne from '@/components/footer/footer-one.vue';
    import ScrollToTop from '@/components/scroll-to-top.vue';

    import bg from '@/assets/img/shortcode/breadcumb.jpg'
    import fallbackImage from '@/assets/img/gallery/shop-01/shop-01.jpg'
    import Aos from 'aos';
    import { storefrontRequest } from '@/lib/storefront-api';

    const portfolios = ref([]);
    const categories = ref([]);
    const activeCategory = ref('All');
    const loading = ref(true);
    const error = ref('');

    const categoryTabs = computed(() => ['All', ...categories.value]);
    const filteredPortfolios = computed(() => {
        if (activeCategory.value === 'All') {
            return portfolios.value;
        }

        return portfolios.value.filter((item) => item.category === activeCategory.value);
    });

    onMounted(async () => {
        Aos.init();
        try {
            const response = await storefrontRequest('/portfolios', { params: { per_page: 24 } });
            portfolios.value = response.data || [];
            categories.value = response.categories || [];
        } catch (e) {
            error.value = e.message || 'Failed to load portfolios.';
        } finally {
            loading.value = false;
        }
    });
</script>
